import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';

/**
 * WorkspaceScope — the shared, VESPER-OWNED state that describes what the sales
 * workspace is currently showing: the worklist filters, the cockpit filters, and the
 * current selection. The worklist and cockpit read from it and write to it.
 *
 * It works identically whether or not Nymeria exists. When Nymeria is on, its view
 * tools are simply an *additional writer* to this same store (e.g. set_workspace_scope
 * dispatches SET_WORKLIST). Turning Nymeria off removes that writer and nothing else.
 * Nothing here imports or depends on Nymeria.
 */

const initialScope = {
  worklist: {
    enrichmentStatus: 'all',   // all | current | stale | none
    keyOnly: false,
    adjOnly: false,
    page: 1,                   // 1-based (Mantine Pagination)
    from: null,                // ISO date string (rfq_create_date >=)
    to: null,                  // ISO date string (rfq_create_date <)
    marketSegment: null,       // MarketSegment filter (null = all)
    customer: '',              // buyer/customer name search
    salesperson: '',           // salesperson CCODE (no list UI; Nymeria-drivable, shared by list + cockpit)
    minScore: 0,               // priority_score floor
    sortField: 'rfq_create_date',  // rfq_create_date | priority_score
    sortDir: 'desc',           // asc | desc
  },
  selection: {
    dealPublicId: null,
    lineIdx: 0,
  },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_WORKLIST':
      // Any filter change resets to page 1 unless the change is itself the page.
      return {
        ...state,
        worklist: {
          ...state.worklist,
          ...action.patch,
          ...(('page' in action.patch) ? {} : { page: 1 }),
        },
      };
    case 'SET_SELECTION':
      return { ...state, selection: { ...state.selection, ...action.patch } };
    case 'RESET_WORKLIST':
      return { ...state, worklist: initialScope.worklist };
    case 'SET_SCOPE':                       // bulk set (used by Nymeria view tools later)
      return { ...state, ...action.scope };
    default:
      return state;
  }
}

const WorkspaceScopeContext = createContext(null);

export function WorkspaceScopeProvider({ children }) {
  const [scope, dispatch] = useReducer(reducer, initialScope);

  const setWorklist  = useCallback((patch) => dispatch({ type: 'SET_WORKLIST', patch }), []);
  const setSelection = useCallback((patch) => dispatch({ type: 'SET_SELECTION', patch }), []);
  const resetWorklist = useCallback(() => dispatch({ type: 'RESET_WORKLIST' }), []);
  const setScope     = useCallback((s) => dispatch({ type: 'SET_SCOPE', scope: s }), []);

  const value = useMemo(
    () => ({ scope, setWorklist, setSelection, resetWorklist, setScope }),
    [scope, setWorklist, setSelection, resetWorklist, setScope],
  );
  return <WorkspaceScopeContext.Provider value={value}>{children}</WorkspaceScopeContext.Provider>;
}

export function useWorkspaceScope() {
  const ctx = useContext(WorkspaceScopeContext);
  if (!ctx) throw new Error('useWorkspaceScope must be used within a WorkspaceScopeProvider');
  return ctx;
}
